// Review detail — what one pull request changed in the agent configuration,
// what the review found, and who pushed it (ADR-016).
//
// The layout answers three questions in order: how bad is it, what exactly
// changed, and who did it. Triage actions live on each finding rather than on
// the review, because a review commonly mixes a real problem with a
// false positive and resolving them together loses that distinction.

import { ArrowLeft, ExternalLink } from "lucide-react";
import { Link } from "@/components/ui/link";
import { useParams } from "@/lib/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  Actor,
  CategoryLabel,
  SeverityBadge,
  Sha,
  SourceBadge,
  VerdictBadge,
  When,
} from "@/components/governance/bits";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { updateFinding } from "@/lib/api/endpoints";
import { usePrReview } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { FindingStatus, GovernanceFindingView } from "@/types/api";

export default function ReviewDetailPage() {
  const params = useParams<{ reviewId: string }>();
  const reviewId = Number(params.reviewId);
  const { data, isLoading, isError, error, refetch } = usePrReview(
    Number.isFinite(reviewId) ? reviewId : null,
  );

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const open = data.findings.filter((f) => f.status === "open");
  const triaged = data.findings.filter((f) => f.status !== "open");

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild className="-ml-2">
        <Link href="/governance">
          <ArrowLeft className="h-4 w-4" /> All reviews
        </Link>
      </Button>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="truncate">
                {data.pr_title ?? `Pull request #${data.pr_number}`}
              </CardTitle>
              <CardDescription className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>
                  {data.repo_full_name} · #{data.pr_number}
                </span>
                <Sha sha={data.head_sha} />
                <Actor login={data.author_login} />
                <When iso={data.created_at} />
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <VerdictBadge verdict={data.verdict} status={data.status} />
              {data.pr_url ? (
                <Button size="sm" variant="outline" asChild>
                  <a href={data.pr_url} target="_blank" rel="noreferrer noopener">
                    Open PR <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {data.changed_file_count} governed {data.changed_file_count === 1 ? "file" : "files"}{" "}
            changed · {data.findings.length} {data.findings.length === 1 ? "finding" : "findings"}
          </p>

          {/* A failed scan must never read as a clean review. */}
          {data.last_error ? (
            <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              This review could not run: {data.last_error}
            </p>
          ) : null}
          {/* Surfaces the backend's own reason rather than a generic line, so a
              missing analyzer model reads as a fixable setting with a link to
              the screen that fixes it — not as an unexplained degradation. */}
          {data.analyzer_error ? (
            <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              <p>
                The AI analysis pass did not complete, so this review reflects the deterministic
                rule checks only. It is not a clean bill of health.
              </p>
              <p className="text-xs">{data.analyzer_error}</p>
              {/analyzer model/i.test(data.analyzer_error) ? (
                <Button size="sm" variant="outline" asChild>
                  <Link href="/governance">Choose an analyzer model</Link>
                </Button>
              ) : null}
            </div>
          ) : null}

          {data.files.length ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {data.files.map((file) => (
                <span
                  key={file.path}
                  className="inline-flex items-center gap-2 rounded-md border border-border px-2 py-1 text-xs"
                >
                  <code>{file.path}</code>
                  <Badge variant="outline">{file.change_type}</Badge>
                  {file.max_severity ? <SeverityBadge severity={file.max_severity} /> : null}
                </span>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {data.findings.length === 0 ? (
        <EmptyState
          title="Nothing risky found in this change"
          hint="The governed files this pull request touches were reviewed and no risks were raised."
        />
      ) : (
        <>
          <Section title="Open findings" findings={open} reviewId={reviewId} />
          {triaged.length ? (
            <Section title="Triaged" findings={triaged} reviewId={reviewId} muted />
          ) : null}
        </>
      )}
    </div>
  );
}

function Section({
  title,
  findings,
  reviewId,
  muted,
}: {
  title: string;
  findings: GovernanceFindingView[];
  reviewId: number;
  muted?: boolean;
}) {
  if (!findings.length) return null;
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title} ({findings.length})
      </h2>
      {findings.map((finding) => (
        <FindingCard key={finding.id} finding={finding} reviewId={reviewId} muted={muted} />
      ))}
    </div>
  );
}

function FindingCard({
  finding,
  reviewId,
  muted,
}: {
  finding: GovernanceFindingView;
  reviewId: number;
  muted?: boolean;
}) {
  const qc = useQueryClient();
  const [note, setNote] = React.useState("");

  const triage = useMutation({
    mutationFn: (status: FindingStatus) => updateFinding(finding.id, status, note || undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.prReview(reviewId) });
      void qc.invalidateQueries({ queryKey: ["my", "governance"] });
    },
  });

  return (
    <Card className={muted ? "opacity-70" : undefined}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <SeverityBadge severity={finding.severity} />
              <CardTitle className="text-base">{finding.title}</CardTitle>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <code>{finding.file_path}</code>
              <CategoryLabel category={finding.category} />
              <SourceBadge source={finding.source} />
              {finding.rule_id ? <code className="opacity-70">{finding.rule_id}</code> : null}
            </div>
          </div>
          {finding.status !== "open" ? (
            <Badge variant="secondary">{finding.status.replace(/_/g, " ")}</Badge>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        <p className="text-sm">{finding.rationale}</p>

        {finding.evidence?.snippet ? (
          <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 text-xs">
            {finding.evidence.snippet}
          </pre>
        ) : null}

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-muted-foreground">Changed by</span>
          <Actor login={finding.actor_login} />
          <Sha sha={finding.commit_sha} />
        </div>

        {finding.resolution_note ? (
          <p className="text-xs text-muted-foreground">Note: {finding.resolution_note}</p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          {finding.status === "open" ? (
            <>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note…"
                className="h-9 min-w-[12rem] flex-1 rounded-md border border-input bg-background px-3 text-sm"
              />
              <Button
                size="sm"
                variant="outline"
                disabled={triage.isPending}
                onClick={() => triage.mutate("acknowledged")}
              >
                Acknowledge
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={triage.isPending}
                onClick={() => triage.mutate("resolved")}
              >
                Resolved
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={triage.isPending}
                onClick={() => triage.mutate("false_positive")}
                title="Stops this finding counting against the review."
              >
                False positive
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={triage.isPending}
              onClick={() => triage.mutate("open")}
            >
              Reopen
            </Button>
          )}
        </div>

        {triage.isError ? <ErrorState error={triage.error} /> : null}
      </CardContent>
    </Card>
  );
}
